export interface News {
    company?: number;
    title: string;
    dateStart: string;
    dateEnd: string;
    document: Document;
    time?: string;
    id: number;
    isArchived: boolean;
    pinned: boolean;
}

export interface CreateNews {
    company?: number;
    title: string;
    dateStart: string;
    dateEnd: string;
    document: Document;
}

export type Role = 'master' | 'handlowiec' | 'dekarz' | 'nadzorca';

export type PageView = 'orders' | 'new-order' | 'settlements' | 'promotions' | 'isod' | 'account' | 'cookie-policy' | 'news' | 'changePassword' | 'admin' | 'additional-tab' | 'settings' | 'urlExpired' | 'register-roofer' | 'register-trader' | 'manage-traders';
export type OperationType = 'editDefaultView' | 'manageOrder' | 'authorizeOrder' | 'changePromotionDeliveryAdress' | 'viewSystemVersion';

export const PermissionFlagNames = ['canDeleteNews', 'canManageNews', 'canManageAdmins', 'canManageServiceWorks', 'canViewTests', 'canManageAssortmentImages', 'canManageCache', 'canViewStatistics', 'canEditAdditionalTab', 'canEditTerms', 'canAccessAdminPanel', 'canUpdatePostals', 'canEditManual'] as const;
export type PermissionFlagName = typeof PermissionFlagNames[number];

export interface PermissionFlags extends Record<PermissionFlagName, boolean> { }

export class Permissions implements PermissionFlags {
    canDeleteNews: boolean = false;

    canManageNews: boolean = false;

    canManageAdmins: boolean = false;

    canManageServiceWorks: boolean = false;

    canViewTests: boolean = false;

    canManageAssortmentImages: boolean = false;

    canManageCache: boolean = false;

    canViewStatistics: boolean = false;

    canEditAdditionalTab: boolean = false;

    canEditTerms: boolean = false;

    canUpdatePostals: boolean = false;

    canAccessAdminPanel: boolean = false;

    canEditManual: boolean = false;

    public constructor(init?: Partial<Permissions>) {
        Object.assign(this, init);
    }

    anyAdminTabPermission() {
        return this.canManageAdmins ||
            this.canManageAssortmentImages ||
            this.canManageCache ||
            this.canManageServiceWorks ||
            this.canViewStatistics ||
            this.canViewTests ||
            this.canEditAdditionalTab ||
            this.canEditTerms ||
            this.canUpdatePostals ||
            this.canEditManual;
    }
}

export interface User {
    id: string;
    login: string;
    username: string;
    email: string;
    role: Role;
    authEnabled: boolean;
    permissions: Permissions;
    name: string;
    phone: string;
    surname: string;
    token: string;
    tokenExpiration: number;
    company: string;
    companyIsod: string;
    guardian: string;
    guardianMail: string;
    guardianPhone: string;
    mag: string;
    logo: string;
    forcePwdChange: boolean;
    environment: string;
    promotionalProgramEnroll: string;
    promotionTermsAccept: string;
    promotionTermsChange: string;
    wydr: string[];
    currency: string;
    params: Param[];
}

export interface UserForISOD {
    company: string;
    logo: string;
    login: string;
    token: string;
    tokenExpiration: number;
}

export type ParamTypes = 'DefaultNewOrderView' | 'DefaultNewOrderViewMobile' | 'DefWindow' | 'DefZamPageCount';
export interface Param {
    ParamName: string;
    ParamValue: string;
}

export interface Jwt {
    token: string;
    tokenExpiration: number;
}

export enum OrderDirection {
    Asc = 'asc',
    Desc = 'desc'
}

export enum OrderStep {
    SelectAssortmentProducts = 1,
    OrderSummary,
    SummaryForm
}

export interface Billing {
    date: string;
    have: number;
    number: string;
    owing: number;
    payment: string;
    paymentDeadline: string;
    lateDays?: number;
    color?: string;
    onBalance: number;
}

export interface SummaryBillingRow {
    owing: number;
    have: number;
    onBalance: number;
}

export interface SummaryBilling {
    sum7orLess: SummaryBillingRow;
    sum8to14: SummaryBillingRow;
    sum15to21: SummaryBillingRow;
    sum22orMore: SummaryBillingRow;
    sum: SummaryBillingRow;
}

export interface SaleAgent {
    logo: string;
    login: string;
    firstName: string;
    lastName: string;
    date: string;
    blocked: boolean;
}

export interface Product {
    symKar: string;
    path: string[];
    description: string;
    descriptionEN: string;
    descriptionRU: string;
    unit: string;
    unitM2: string;
    isDimension: boolean;
    dimensionDefault: number;
    dimensionCanEdit: boolean;
    dimensionMin: number | null;
    dimensionMax: number | null;
    remarks: string;
    additions: ProductAddition[];
    priceInfo: ProductPrice;
    additionalAssortment: AdditionalProduct[];
    isEditable?: boolean;
    image: string | null;
    converterM2: number;
    converterMB: number;
    coating: string;
    surface: string;
    color: string;
}

export interface ProductInGroup {
    symKar: string;
    path: string[];
    description: string;
    descriptionEN: string;
    descriptionRU: string;
    unit: string;
    image: string;
}

export interface FoundProduct {
    symKar: string;
    description: string;
    unit: string;
    image: string | null;
}

export interface AdditionalProductGrouped {
    symKar: string;
    description: string;
    group1: string;
    group2: string;
    group3: string;
    units: UnitPriceData[];
}

export interface AdditionalProduct {
    symKar: string;
    description: string;
    group1: string;
    group2: string;
    group3: string;
    converterM2: number;
    isDimension: boolean;
    isDefault: boolean;
    dimensionDefault: number;
    price: number;
    discount: number;
    unit: string;
}

export interface UnitPriceData {
    productIndex: number;
    converterM2: number;
    isDimension: boolean;
    isDefault: boolean;
    dimensionDefault: number;
    price: number;
    discount: number;
    unit: string;
}

export interface ProductAddition {
    price: number;
    name: string;
    default: boolean;
    chosen: boolean | null;
    value: string;
    parName: string;
    setOfValues: string;
    parId: string;
}

export interface Unit {
    unit: string;
    isDefault: boolean;
    converter: number;
    description: string;
}

export interface ProductPrice {
    price: number;
    discount: number;
    vatRate: number;
    units: Unit[];
}

export interface Order {
    id: string;
    orderNumber: string;
    customerOrderNumber: string;
    date: string;
    status: string;
    authorizationStatus: string;
    description: string;
    customerDescription: string;
    gross: number;
    net: number;
    vat: number;
    weight: number;
    isEditable: boolean;
    isDeletable: boolean;
    isPrintable: boolean;
}

export interface ProductInOrder {
    product: Product;
    additionsSelected: boolean[] | null;
    dimension: number | null;
    quantity: number;
    unit: string;
}

export interface DetailedOrder {
    products: ProductInOrder[];
    orderId: string;
    orderNumber?: string;
    customerOrderNumber?: string;
    description: string;
    customerDesc: string;
    address: UserDeliveryAddress | undefined;
    contact?: string;
    phoneNumber: string;
    rooferId?: number;
    rooferLogin: string | null;
    status?: string;
    addToExisting: boolean;
    productsNotAvailable?: boolean;
}

export type Option = '' | 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';

export interface PromotionalProduct {
    symKar: string;
    photo: string;
    description: string;
    price: number;
    points: number;
    options: Option[];
    group: string;
    isElectronicShipping: boolean;
}

export interface PromotionalOrder {
    date: string;
    quantity: number;
    description: string;
    price: number;
    points: number;
}

export interface PromotionalDelivery {
    symKar: string;
    delivery: string;
    description: string;
    points: number;
    price: number;
}

export type DeliveryOption = 'inputAddress' | 'distributorAddress';

export interface AdditionalAssortmentGroup {
    description: string;
    path: string;
    image: string | null;
    price: number;
    discount: number;
}

export interface ProductsGroup {
    description: string;
    level: number;
    path: string;
    hasProducts: boolean;
    groups: string[];
    image: string | null;
}

export interface GroupsAndProducts {
    productsInGroup: ProductInGroup[];
    products: Product[];
    groups: ProductsGroup[];
}

export interface RegisteredUserData {
    login: string;
    password: string;
}

export enum CartGroup {
    main = 'main',
    gutterings = 'gutterings',
    isod = 'isod'
}

export interface ProductInLocalCart {
    product: Product;
    quantity: number;
    dimension: number | null;
    additionsSelected: boolean[] | null;
    mainProductSymkar: string | null;
    unit: Unit;
    cartGroup: CartGroup;
    systemData: GutteringSystemData | null;
}

export interface LocalCart {
    productsInLocalCart: ProductInLocalCart[];
}

export interface ImportedProduct {
    symKar: string,
    quantity: number;
    dimension: number | null;
    additionsSelected: string[];
    systemData: GutteringSystemData | null;
    cartGroup: CartGroup;
    unit: string;
}

export interface RooferInfo {
    login: string;
    company: string;
    nip: string;
    phone: string;
    error?: string;
    serverError?: boolean;
}

export interface AddOrderRequestDTO {
    login: string;
    logo: string;
    description: string;
    orderId?: string | null;
    customerOrderNumber?: string;
    address: string;
    contact: string;
    phoneNumber: string;
    roofer: string | null;
    customerDesc: string;
    addToExisting: boolean;
    products: {
        symKar: string;
        quantity: number;
        unit: string;
        dimension: number | undefined;
        additionsSelected: (ProductAddition | null)[];
    }[];
}

export interface AppendOrderRequestDTO extends AddOrderRequestDTO {
    nagIdSource: string;
}

export interface TermsAccepted {
    DATA: {
        Mes: string;
        Error: string;
    };
}

export interface TermsResponse {
    date: Date,
    document: Document;
    type: string;
}

export type ViewId = 'basic' | 'advanced' | 'treatment' | 'gutters' | 'plate';

export interface NotificationComponentData {
    isVisible: boolean;
    message: string;
    variant: string;
}

export interface GutteringSystemData {
    gutterShape: string;
    material: string;
    grille: string;
    tubeShape: string;
    size: string;
    colorName: string;
}

export interface GutteringProductCount {
    unit: Unit;
    count: number;
}

export interface WebserviceTestResult {
    date: string;
    testCasesResults: TestCaseResult[];
}

export interface TestCaseResult {
    className: string;
    exceptionMessage: string;
    prettifiedClassName: string;
    prettifiedMethodName: string;
    size: string;
    status: string;
    time: string;
}

export interface ProductInCartTable extends ProductInLocalCart {
    groupPath: string;
    indexInCart: number;
    quantityJm: number;
    quantityM2: number;
    price: number;
}

export interface AdditionalItemsSummary {
    quantity: number;
    netto: number;
    vat: number;
    gross: number;
}

export interface CartTableItem {
    title: string;
    identifier: string;
    products: ProductInCartTable[];
    totalPrice: number;
    totalNetto: number;
    totalVat: number;
    totalQuantity: number;
    totalQuantityJM: number;
    totalQuantityM2: number;
    additionalItemsSummary: AdditionalItemsSummary;
}

export interface CartTableContent {
    mainItems: { [key: string]: CartTableItem };
    additionalItems: { [key: string]: { [key: string]: CartTableItem } };
    isodItems: { [key: string]: CartTableItem };
    gutteringsItems: { [key: string]: CartTableItem };
    itemsCount: number;
    summaryPrice: number;
    summaryDiscount: number;
    summaryPriceBeforeDiscount: number;
    summaryVat: number;
}

export interface OrderStatusLine {
    orderId: string;
    catalogueName: string;
    quantity: number;
    unit: string;
    producer: string;
    status: string;
    weight: number;
}

export interface OrderStatusHeader {
    orderId: string;
    orderNumber: string;
    status: string;
    deliveryAddress: string;
    clientNumber: string;
    FAnumbers: string;
    name: string;
    date: Date;
    remarks: string;
    description: string;
    net: number;
    vat: number;
    gross: number;
    additional: boolean;
    roofer: string;
    trader: string;
    logo: string;
    weight: number;
}

export interface OrderStatus {
    header: OrderStatusHeader;
    lines: OrderStatusLine[];
}

export type ISODAction = 'ISODClosed' | 'ISODPoll' | 'ISODSendProducts' | 'ISODReplaceProducts' | 'ISODMergeProducts';

export interface ISODPackage {
    action: ISODAction,
    data?: ISODPackageData | boolean | object
}

export interface ISODPackageData {
    antykondensat: boolean,
    asortyment: ISODProduct[],
    folia: boolean,
    kurs: number,
    scalKoszyk: boolean,
    soundproofMat: boolean,
    waluta: string,
    additions: string[]
}

export interface ISODProduct {
    symkar: string,
    ilosc: number,
    dlugosc: string,
    jm: string
}

export interface ServiceWorkType {
    id: Number,
    from: Date,
    to: Date,
    message: string
}
export const OrderStatusNames = ['accepted', 'unauthorized', 'waiting', 'processing', 'completed', 'all'] as const;
export type OrderStatusName = typeof OrderStatusNames[number];

export const OrderStatusNameToLabel: Record<OrderStatusName, string> = {
    unauthorized: 'Do autoryzacji',
    accepted: 'Przyjęte',
    waiting: 'Oczekujące',
    processing: 'Realizowane',
    completed: 'Zrealizowane',
    all: 'Wszystkie'
} as const;

export const OrderStatusLabelToName: Record<string, OrderStatusName> = {
    Przyjęte: 'accepted',
    DoAutoryzacji: 'unauthorized',
    Oczekujące: 'waiting',
    Realizowane: 'processing',
    Zrealizowane: 'completed',
    Wszystkie: 'all'
} as const;

export type CacheManagementSource = 'zamowienia' | 'isod';
export type CacheManagementStatus = 'running' | 'finished' | 'error';

export interface CacheManagement {
    source: CacheManagementSource;
    status: CacheManagementStatus;
    last_execution_time: number;
    progress: number;
    description: string;
    id: number,
    date: Date | null,
    name: string
}

export interface EditAssortmentImage {
    message: string,
    error: boolean,
    filename: string,
}

export interface StatisticDay {
    endpoints_requests: { [key: string]: number };
    hour_requests: number[];
    total_requests: number;
    users: string[];
    total_errors: number;
}

export interface Statistics {
    [key: string]: StatisticDay;
}

export interface SchemaValidationResults {
    [key: string]: SchemaValidationResultsByProcedureName;
}

export interface SchemaValidationResultsByProcedureName {
    [key: string]: SchemaValidationResultsData;
}

export interface SchemaValidationResultsData {
    data: string[];
    numberOfErrors: number;
}

export interface SchemaValidationResultsResponse {
    results: SchemaValidationResults,
    errorsOccured: boolean
}

export interface SchemaValidationLatestResultsResponse {
    results: SchemaValidationLatestResult[],
    errorsOccured: boolean
}

export interface SchemaValidationLatestResult {
    date: string;
    procedure_name: string;
    error: string;
}

export interface AdditionalTab {
    isVisible: boolean;
    document: Document;
    name: string;
}

export interface Document {
    links: string[];
    content: string;
    id?: number;
}

export interface AddOrderResponse {
    NagId: string;
    Numer: string;
}

export interface TownOptionType {
    label: string;
    value: string;
}

export class UserDeliveryAddress {
    streetType: string = '';
    streetName: string = '';
    houseNumber: string = '';
    apartmentNumber: string = '';
    zipCode: string = '';
    town: string = '';
    country: string = '';
    description: string = '';
    special: boolean = false;
    readOnly: boolean = false;
    fullAddress: boolean = true; // non full address has streettype, streetname, housenumber and apartmentNumber empty
    id?: string;
    default: boolean = false;
    constructor(init?: Partial<UserDeliveryAddress>) {
        Object.assign(this, init);
    }
    toAddressString = () => {
        if (this.special) { return this.description; }
        if (!this.fullAddress) { return `${this.zipCode} ${this.town}`.trim(); }
        let address = `${this.streetName} ${this.houseNumber}`;
        address = this.streetType !== '' ? `${this.streetType} ${address}` : address;
        address = this.apartmentNumber !== '' ? `${address}/${this.apartmentNumber}` : address;
        return `${address}, ${this.zipCode} ${this.town}`.trim();
    };
}

export interface ShutdownHours {
    from: number;
    to: number;
}

export interface OptionType<T> {
    label: string;
    value: T;
}

export const NUMBER_OF_TESTS_RESULTS_ON_DESKTOP = 5;
export const NUMBER_OF_TESTS_RESULTS_ON_MOBILE = 2;
export const NUMBER_OF_LATEST_SCHEMA_VALIDATION_RESULTS = 15;

export interface IncorrectImportedProduct {
    description: string;
    quantity: number;
    unit: string;
}
